package com.shadowings.gero.data.lessons

import com.shadowings.gero.data.repository.AdjectivePairs
import com.shadowings.gero.domain.model.Option
import com.shadowings.gero.domain.model.SingleSelectionContent
import com.shadowings.gero.domain.model.WordSelectionContent
import com.shadowings.gero.presentation.model.Word
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.quiz_ripasso_quale_gero_significa
import kotlin.random.Random

// Noun + adjective generator (G1 in CAPITOLI_16_18_PIANO.md). words.tsv only holds the
// masculine singular of an adjective: every other form is derived here from the noun's gender
// and number, both in Egyptian (-t / -w / -wt) and in the translation.

data class NounAdjectivePhrase(
    val hieroglyphs: String,
    val transliteration: String,
    val correctPhrase: String,
    // Other word orders that are just as right in the target language.
    val alternativePhrases: List<String>,
    val correctTokens: List<String>,
    val noun: Word,
    val adjective: Word
)

// Determinatives stay at the end of the word: the ending goes before them (nḏs + t = nḏs.t + G37).
private val ADJECTIVE_DETERMINATIVES = setOf("G37", "Y1")

private fun egyptianEnding(feminine: Boolean, plural: Boolean): String? = when {
    feminine && plural -> "X1:Z2"
    plural -> "G43"
    feminine -> "X1"
    else -> null
}

fun inflectAdjectiveGardiner(adjective: Word, feminine: Boolean, plural: Boolean): String {
    val ending = egyptianEnding(feminine, plural) ?: return adjective.gardiner
    val gardiner = adjective.gardiner
    val lastSeparator = gardiner.indexOfLast { it == '-' || it == ':' || it == '*' }
    val lastSign = gardiner.substring(lastSeparator + 1)
    return if (lastSeparator > 0 && lastSign in ADJECTIVE_DETERMINATIVES) {
        "${gardiner.substring(0, lastSeparator)}-$ending-$lastSign"
    } else {
        "$gardiner-$ending"
    }
}

fun inflectAdjectiveReading(adjective: Word, feminine: Boolean, plural: Boolean): String =
    adjective.reading + when {
        feminine && plural -> "wt"
        plural -> "w"
        feminine -> "t"
        else -> ""
    }

// The gender the translation agrees with, which isn't always the Egyptian one (pr is masculine,
// "la casa" feminine).
private fun isFeminineIn(noun: Word, languageCode: String): Boolean = when (languageCode) {
    "it" -> noun.isFeminineIt
    "fr" -> noun.isFeminineFr
    "es" -> noun.getArticolo("es")?.trim() in setOf("la", "las")
    else -> noun.isFeminine
}

private fun adjectiveBase(adjective: Word, languageCode: String): String =
    adjective.getMeaning(languageCode).split("/")[0].trim().lowercase()

// Word order in the translation. English and German always put the adjective first. Italian
// takes either order with every adjective ("la sorella cattiva" / "la cattiva sorella"); Spanish
// and French only with the adjectives words.tsv marks in adj_order_es / adj_order_fr: "both" =
// after the noun first, before accepted; "pre" = before only ("le bon pain", never "le pain bon").
private const val ORDER_BOTH = "both"
private const val ORDER_PRE = "pre"

private fun Word.allowsPrenominal(languageCode: String): Boolean {
    val order = if (languageCode == "es") adjOrderEs else adjOrderFr
    return order == ORDER_BOTH || order == ORDER_PRE
}

fun inflectAdjective(adjective: Word, languageCode: String, feminine: Boolean, plural: Boolean): String {
    val base = adjectiveBase(adjective, languageCode)
    return when (languageCode) {
        "it" -> when {
            base.endsWith("o") -> base.dropLast(1) + when {
                feminine && plural -> "e"
                plural -> "i"
                feminine -> "a"
                else -> "o"
            }
            base.endsWith("e") && plural -> base.dropLast(1) + "i"
            else -> base
        }
        "es" -> when {
            base.endsWith("o") -> base.dropLast(1) + when {
                feminine && plural -> "as"
                plural -> "os"
                feminine -> "a"
                else -> "o"
            }
            base.endsWith("e") -> if (plural) base + "s" else base
            else -> if (plural) base + "es" else base
        }
        "fr" -> {
            val gendered = when {
                !feminine || base.endsWith("e") -> base
                base.endsWith("on") -> base + "ne"
                else -> base + "e"
            }
            if (plural && !gendered.endsWith("s") && !gendered.endsWith("x")) gendered + "s" else gendered
        }
        // Weak declension, always after a definite article: "der gute Mann", "die guten Männer".
        "de" -> base + if (plural) "en" else "e"
        else -> base
    }
}

private fun withArticle(article: String?, text: String): String = when {
    article.isNullOrBlank() || article == "-" -> text
    article.endsWith("'") -> "$article$text"
    else -> "$article $text"
}

private fun startsWithVowel(word: String): Boolean =
    word.lowercase().firstOrNull()?.let { it in "aeiouàâéèêìîòôùûh" } == true

// Before the adjective the article follows the adjective, not the noun: "l'uomo" → "il buon uomo".
private fun italianArticle(feminine: Boolean, plural: Boolean, next: String): String {
    val w = next.lowercase()
    val impure = w.startsWith("z") || w.startsWith("gn") || w.startsWith("ps") ||
        (w.length > 1 && w[0] == 's' && w[1] !in "aeiou")
    return when {
        feminine && plural -> "le"
        feminine -> if (startsWithVowel(w)) "l'" else "la"
        plural -> if (startsWithVowel(w) || impure) "gli" else "i"
        startsWithVowel(w) -> "l'"
        impure -> "lo"
        else -> "il"
    }
}

private fun italianPrenominal(noun: Word, nounText: String, adjectiveWord: Word, adjectiveText: String): String {
    val feminine = noun.isFeminineIt
    val impureNoun = italianArticle(false, false, nounText) == "lo"
    // words.tsv apocope_it: the shortened form before a masculine singular noun, like the article
    // "uno": "il buon padre", but "il buono scriba".
    val apocope = adjectiveWord.apocopeIt?.takeIf { !feminine && !noun.isPlural && !impureNoun }
    val adjective = apocope ?: adjectiveText
    return withArticle(italianArticle(feminine, noun.isPlural, adjective), "$adjective $nounText")
}

private fun spanishPrenominal(noun: Word, article: String?, nounText: String, adjective: Word, adjectiveText: String): String? {
    if (!adjective.allowsPrenominal("es")) return null
    // words.tsv apocope_es, before a singular noun: "buen hombre", "mal siervo", "gran casa". An
    // adjective in -o has its own feminine, which stays whole ("buena mujer"); the others
    // shorten with both genders ("gran mujer").
    val genderless = !adjectiveBase(adjective, "es").endsWith("o")
    val shortened = adjective.apocopeEs
        ?.takeIf { !noun.isPlural && (genderless || !isFeminineIn(noun, "es")) }
        ?: adjectiveText
    return withArticle(article, "$shortened $nounText")
}

private fun frenchPrenominal(noun: Word, nounText: String, adjectiveText: String): String {
    val article = when {
        noun.isPlural -> "les"
        startsWithVowel(adjectiveText) -> "l'"
        noun.isFeminineFr -> "la"
        else -> "le"
    }
    return withArticle(article, "$adjectiveText $nounText")
}

fun buildNounAdjectivePhrase(noun: Word, adjective: Word, languageCode: String): NounAdjectivePhrase {
    val egyptianFeminine = noun.isFeminine
    val plural = noun.isPlural
    val nounText = getCleanMeaning(noun, languageCode)
    val article = noun.getArticolo(languageCode)
    val adjectiveText = inflectAdjective(adjective, languageCode, isFeminineIn(noun, languageCode), plural)
    val postnominal = "${withArticle(article, nounText)} $adjectiveText"

    val orders: List<String> = when (languageCode) {
        "it" -> listOf(postnominal, italianPrenominal(noun, nounText, adjective, adjectiveText))
        "es" -> listOfNotNull(postnominal, spanishPrenominal(noun, article, nounText, adjective, adjectiveText))
        "fr" -> {
            val prenominal = frenchPrenominal(noun, nounText, adjectiveText)
            when {
                !adjective.allowsPrenominal("fr") -> listOf(postnominal)
                // words.tsv fr_post_adjectives on the noun: "le bon homme" reads as "le
                // bonhomme" and "la bonne femme" as "the dame", so "l'homme bon" first.
                adjective.id in noun.frPostAdjectives -> listOf(postnominal, prenominal)
                adjective.adjOrderFr == ORDER_PRE -> listOf(prenominal)
                else -> listOf(postnominal, prenominal)
            }
        }
        else -> listOf(withArticle(article, "$adjectiveText $nounText"))
    }
    val phrase = orders.first()

    return NounAdjectivePhrase(
        hieroglyphs = "${noun.gardiner}-${inflectAdjectiveGardiner(adjective, egyptianFeminine, plural)}",
        transliteration = "${noun.reading} ${inflectAdjectiveReading(adjective, egyptianFeminine, plural)}",
        correctPhrase = phrase,
        alternativePhrases = orders.drop(1).distinct().filter { it != phrase },
        correctTokens = tokenizePhrase(phrase),
        noun = noun,
        adjective = adjective
    )
}

// Every valid pair in adjectives.tsv, once each even if a noun is listed in several rows.
// plural = true/false keeps only plural/singular nouns; null keeps both.
fun calculateNounAdjectivePhrases(
    words: List<Word>,
    pairs: List<AdjectivePairs>,
    languageCode: String,
    plural: Boolean? = null
): List<NounAdjectivePhrase> {
    // Only words the player has already met: adjectives.tsv can list nouns from later lessons.
    val wordMap = DistractorPool.words(words).associateBy { it.id }
    return pairs
        .flatMap { row -> row.nouns.flatMap { noun -> row.adjectives.map { noun to it } } }
        .distinct()
        .mapNotNull { (nounId, adjectiveId) ->
            val noun = wordMap[nounId] ?: return@mapNotNull null
            val adjective = wordMap[adjectiveId] ?: return@mapNotNull null
            if (plural != null && noun.isPlural != plural) return@mapNotNull null
            buildNounAdjectivePhrase(noun, adjective, languageCode)
        }
}

// Hieroglyphs → build the translation. The wrong tiles are the other forms of the same
// adjective (so the player has to pick the one that agrees), plus another noun and adjective.
fun buildNounAdjectiveWordSelection(
    phrase: NounAdjectivePhrase,
    otherNouns: List<Word>,
    otherAdjectives: List<Word>,
    languageCode: String,
    random: Random = Random
): WordSelectionContent {
    val otherForms = listOf(false to false, true to false, false to true, true to true)
        .map { (feminine, plural) -> inflectAdjective(phrase.adjective, languageCode, feminine, plural) }

    val otherNoun = DistractorPool.words(otherNouns)
        .filter { it.id != phrase.noun.id }
        .randomOrNull(random)
    val otherAdjective = otherAdjectives
        .filter { it.id != phrase.adjective.id }
        .randomOrNull(random)

    val wrongTokens = buildList {
        addAll(otherForms)
        otherNoun?.let { addAll(tokenizePhrase(withArticle(it.getArticolo(languageCode), getCleanMeaning(it, languageCode)))) }
        otherAdjective?.let {
            add(inflectAdjective(it, languageCode, isFeminineIn(phrase.noun, languageCode), phrase.noun.isPlural))
        }
    }

    // Same rule as the genitive quizzes: a distractor equal to a needed tile would swallow it.
    val tiles = (phrase.correctTokens + wrongTokens.filter { wrong ->
        phrase.correctTokens.none { it.trim().lowercase() == wrong.trim().lowercase() }
    }.distinct())
        .filter { it.isNotBlank() }
        .shuffled(random)

    return WordSelectionContent(
        hieroglyphs = phrase.hieroglyphs,
        correctPhrase = phrase.correctPhrase,
        alternativePhrases = phrase.alternativePhrases,
        words = tiles,
        transliteration = phrase.transliteration
    )
}

// Translation → pick the right hieroglyphs. Wrong options: the adjective in the other gender, a
// different adjective applied to the same noun and, once plurals are taught, the adjective in the
// other number. Deliberately NOT a noun/adjective column-order swap: MdCWord only mirrors
// individual glyphs when rtl=true (never set here), so a plain reversed-order string renders as
// the two columns swapped with nothing else about them flipped — indistinguishable from reading
// the correct phrase in the other direction whenever the glyphs involved are left-right symmetric
// (the game's own ch.3 lesson on symmetric signs explains why: bread/water give no directional
// cue). That made the swap either read as a second copy of the right answer or as a wrong answer
// that "looks right", so it was replaced with a same-order, different-adjective option instead.
fun buildNounAdjectiveFormChoice(
    phrase: NounAdjectivePhrase,
    includeNumber: Boolean,
    otherAdjectives: List<Word>,
    random: Random = Random
): SingleSelectionContent {
    val noun = phrase.noun
    val feminine = noun.isFeminine
    val plural = noun.isPlural

    val otherAdjective = otherAdjectives
        .filter { it.id != phrase.adjective.id }
        .randomOrNull(random)

    val wrong = buildList {
        add("${noun.gardiner}-${inflectAdjectiveGardiner(phrase.adjective, !feminine, plural)}")
        otherAdjective?.let { add("${noun.gardiner}-${inflectAdjectiveGardiner(it, feminine, plural)}") }
        if (includeNumber) add("${noun.gardiner}-${inflectAdjectiveGardiner(phrase.adjective, feminine, !plural)}")
    }.filter { it != phrase.hieroglyphs }.distinct()

    return SingleSelectionContent(
        text = Res.string.quiz_ripasso_quale_gero_significa,
        textArgs = listOf(phrase.correctPhrase),
        detail = "",
        options = (listOf(Option(phrase.hieroglyphs, correct = true)) + wrong.map { Option(it, correct = false) })
            .shuffled(random)
    )
}

// An adjective used as a noun (AGGETTIVO_SOSTANTIVATO): masculine nfr "the good one", feminine
// nfrt "the good thing". Each language has its own way to say it: German and Spanish have a
// neuter ("das Gute", "lo bueno"), Italian, English and French need "cosa"/"thing"/"chose".
fun substantivizedAdjectiveLabel(adjective: Word, languageCode: String, feminine: Boolean): String {
    val base = adjectiveBase(adjective, languageCode)
    val masculine = inflectAdjective(adjective, languageCode, feminine = false, plural = false)
    return when (languageCode) {
        "it" -> if (feminine) {
            "la cosa ${inflectAdjective(adjective, languageCode, feminine = true, plural = false)}"
        } else {
            withArticle(italianArticle(feminine = false, plural = false, next = masculine), masculine)
        }
        "de" -> {
            val noun = masculine.replaceFirstChar { it.uppercase() }
            if (feminine) "das $noun" else "der $noun"
        }
        "es" -> if (feminine) "lo $masculine" else "el $masculine"
        "fr" -> if (feminine) {
            val adjectiveText = inflectAdjective(adjective, languageCode, feminine = true, plural = false)
            if (adjective.allowsPrenominal("fr")) {
                withArticle(if (startsWithVowel(adjectiveText)) "l'" else "la", "$adjectiveText chose")
            } else {
                "la chose $adjectiveText"
            }
        } else {
            withArticle(if (startsWithVowel(masculine)) "l'" else "le", masculine)
        }
        else -> if (feminine) "the $base thing" else "the $base one"
    }
}

// A noun with its adjective inside a whole sentence, every right word order (the first is the
// one the quiz shows). English always has an article like formatEnglishSubject. asObject: German
// accusative ("den guten Sohn", weak nouns too: "den starken Soldaten") and the Spanish personal
// "a" ("al padre bueno", "al buen padre").
fun adjectivePhraseTranslation(noun: Word, adjective: Word, languageCode: String, asObject: Boolean = false, animalIsThing: Boolean = false): List<String> {
    val phrase = buildNounAdjectivePhrase(noun, adjective, languageCode)
    val orders = listOf(phrase.correctPhrase) + phrase.alternativePhrases
    return when (languageCode) {
        "en" -> if (noun.getArticolo("en").isNullOrBlank()) orders.map { "the $it" } else orders
        "de" -> {
            if (!asObject || noun.isPlural || noun.getArticolo("de")?.trim() != "der") return orders
            val nounText = germanObliqueSingular(noun, getCleanMeaning(noun, "de"))
            listOf("den ${adjectiveBase(adjective, "de")}en $nounText")
        }
        "es" -> if (asObject && needsSpanishPersonalA(noun, animalIsThing)) {
            orders.map { if (it.startsWith("el ")) "al ${it.removePrefix("el ")}" else "a $it" }
        } else {
            orders
        }
        else -> orders
    }
}

// The noun phrase with the adjective in the Egyptian of a sentence: rmṯ nfr, st nfrt, rmṯw nfrw.
fun adjectiveHieroglyphs(noun: Word, adjective: Word): String =
    "${noun.gardiner}-${inflectAdjectiveGardiner(adjective, noun.isFeminine, noun.isPlural)}"

fun adjectiveTransliteration(noun: Word, adjective: Word): String =
    "${noun.reading} ${inflectAdjectiveReading(adjective, noun.isFeminine, noun.isPlural)}"

// An adjective adjectives.tsv allows for the noun, among the words the player has met, or null
// (the noun then goes without).
fun pickAdjective(noun: Word, words: List<Word>, pairs: List<AdjectivePairs>, random: Random): Word? {
    val adjectiveIds = pairs.filter { noun.id in it.nouns }.flatMap { it.adjectives }.distinct()
    return DistractorPool.words(words).filter { it.id in adjectiveIds }.randomOrNull(random)
}

// Every form of the adjectives the player has met, for wrong tiles ("buono", "buona", "buoni"...).
fun adjectiveForms(words: List<Word>, pairs: List<AdjectivePairs>, languageCode: String): List<String> {
    val adjectiveIds = pairs.flatMap { it.adjectives }.toSet()
    return DistractorPool.words(words).filter { it.id in adjectiveIds }.flatMap { adjective ->
        listOf(false to false, true to false, false to true, true to true).map { (feminine, plural) ->
            inflectAdjective(adjective, languageCode, feminine, plural)
        } + if (languageCode == "de") listOf("${adjectiveBase(adjective, "de")}er", "${adjectiveBase(adjective, "de")}es") else emptyList()
    }.distinct()
}

// The builders' haveAdjective: the adjective goes on one noun of the sentence, never one that
// already has a possessive (callers pass null for those). subject/obj are the free first subject
// and first object; the result is (adjective of the subject, adjective of the object), at most one
// of them set, both null if neither noun can take one.
fun pickSentenceAdjective(
    subject: Word?,
    obj: Word?,
    words: List<Word>,
    pairs: List<AdjectivePairs>,
    random: Random
): Pair<Word?, Word?> {
    val options = listOfNotNull(
        subject?.let { pickAdjective(it, words, pairs, random) }?.let { it to null },
        obj?.let { pickAdjective(it, words, pairs, random) }?.let { null to it }
    )
    return options.randomOrNull(random) ?: (null to null)
}

// The other right word orders of a whole sentence, from the noun phrase with the adjective in it.
fun adjectiveAlternatives(
    sentence: String,
    noun: Word,
    adjective: Word,
    languageCode: String,
    asObject: Boolean,
    animalIsThing: Boolean = false
): List<String> {
    val phrases = adjectivePhraseTranslation(noun, adjective, languageCode, asObject, animalIsThing)
    val shown = phrases.first()
    if (shown !in sentence) return emptyList()
    return phrases.drop(1).map { sentence.replaceFirst(shown, it) }.filter { it != sentence }.distinct()
}
